import React from 'react'
import { Clock, User, Edit3 } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'

export interface TimelineEntry {
  id: string
  type: 'status_changed' | 'stage_changed' | 'contact_made' | 'field_changed'
  userName?: string
  userEmail?: string
  timestamp: string | Date
  // Para status/stage changes
  fromStatus?: string
  toStatus?: string
  fromFunnelStageId?: string
  toFunnelStageId?: string
  // Para contact_made
  contactChannel?: string
  contactTarget?: string
  // Para field_changed
  metadata?: {
    fieldName?: string
    fieldLabel?: string
    oldValue?: any
    newValue?: any
    event?: string
  }
}

interface TimelineHistoryProps {
  entries: TimelineEntry[]
  loading?: boolean
  emptyMessage?: string
}

const getEventIcon = (type: string) => {
  switch (type) {
    case 'status_changed':
    case 'stage_changed':
      return <Edit3 className="h-4 w-4" />
    case 'contact_made':
      return <User className="h-4 w-4" />
    case 'field_changed':
      return <Edit3 className="h-4 w-4" />
    default:
      return <Clock className="h-4 w-4" />
  }
}

const getEventColor = (type: string) => {
  switch (type) {
    case 'status_changed':
      return 'bg-blue-500'
    case 'stage_changed':
      return 'bg-purple-500'
    case 'contact_made':
      return 'bg-green-500'
    case 'field_changed':
      return 'bg-orange-500'
    default:
      return 'bg-gray-500'
  }
}

const formatFieldValue = (value: any): string => {
  if (value === null || value === undefined || value === '') {
    return '(vazio)'
  }
  if (typeof value === 'boolean') {
    return value ? 'Sim' : 'Não'
  }
  if (typeof value === 'object') {
    return JSON.stringify(value)
  }
  return String(value)
}

const getEventDescription = (entry: TimelineEntry): React.ReactElement => {
  switch (entry.type) {
    case 'status_changed':
      return (
        <div>
          <span className="font-medium">Status alterado:</span>{' '}
          <span className="text-muted-foreground">{entry.fromStatus || '(nenhum)'}</span>
          {' → '}
          <span className="text-foreground font-medium">{entry.toStatus}</span>
        </div>
      )

    case 'stage_changed':
      return (
        <div>
          <span className="font-medium">Etapa do funil alterada</span>
        </div>
      )

    case 'contact_made':
      return (
        <div>
          <span className="font-medium">Contato realizado</span>
          {entry.contactChannel && (
            <span className="text-muted-foreground">
              {' '}via {entry.contactChannel}
            </span>
          )}
          {entry.contactTarget && (
            <span className="text-muted-foreground">
              {' '}para {entry.contactTarget}
            </span>
          )}
        </div>
      )

    case 'field_changed':
      const fieldLabel = entry.metadata?.fieldLabel || entry.metadata?.fieldName || 'Campo'
      const oldValue = formatFieldValue(entry.metadata?.oldValue)
      const newValue = formatFieldValue(entry.metadata?.newValue)

      if (entry.metadata?.event === 'lead_created' || entry.metadata?.event === 'vehicle_created') {
        return (
          <div>
            <span className="font-medium">
              {entry.metadata.event === 'lead_created' ? 'Lead criado' : 'Veículo criado'}
            </span>
          </div>
        )
      }

      return (
        <div>
          <span className="font-medium">{fieldLabel} alterado:</span>{' '}
          <span className="text-muted-foreground">{oldValue}</span>
          {' → '}
          <span className="text-foreground font-medium">{newValue}</span>
        </div>
      )

    default:
      return <div className="text-muted-foreground">Evento desconhecido</div>
  }
}

export const TimelineHistory: React.FC<TimelineHistoryProps> = ({
  entries,
  loading = false,
  emptyMessage = 'Nenhum histórico disponível'
}) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (entries.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Clock className="h-12 w-12 mx-auto mb-2 opacity-50" />
        <p>{emptyMessage}</p>
      </div>
    )
  }

  return (
    <div className="relative">
      {/* Timeline line */}
      <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-border" />

      {/* Timeline entries */}
      <div className="space-y-6">
        {entries.map((entry, index) => (
          <div key={entry.id || index} className="relative flex gap-4">
            {/* Icon */}
            <div className={`
              relative z-10 flex-shrink-0 w-10 h-10 rounded-full
              ${getEventColor(entry.type)}
              flex items-center justify-center text-white shadow-md
            `}>
              {getEventIcon(entry.type)}
            </div>

            {/* Content */}
            <div className="flex-1 pt-1 pb-4">
              {/* Event description */}
              <div className="text-sm mb-1">
                {getEventDescription(entry)}
              </div>

              {/* Meta info */}
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                {entry.userName && (
                  <div className="flex items-center gap-1">
                    <User className="h-3 w-3" />
                    <span>{entry.userName}</span>
                  </div>
                )}
                <div className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  <span>
                    {formatDistanceToNow(new Date(entry.timestamp), {
                      addSuffix: true,
                      locale: ptBR
                    })}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
